import { ClickAwayListener } from "@nova/component-click-away-listener"
import classNames from "classnames"
import { selectActiveImageIds } from "features/map/layers/selectors"
import { setActiveImageId } from "features/map/layers/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useState } from "react"
import { useUploadFlowContext } from "../useUploadFlowContext"

export const PreviewFilesWarningsAndErrors = () => {
    const dispatch = useAppDispatch()
    const { uploadFlowWarnings, flightId, imagePreviewType } = useUploadFlowContext()
    const warnings = uploadFlowWarnings?.[flightId]

    const [activeTagId, setActiveTagId] = useState<number | undefined>()

    const activeImageIds = useAppSelector(selectActiveImageIds)
    useEffect(() => {
        if (Object.keys(activeImageIds).length === 0 || Object.values(activeImageIds).length === 1)
            setActiveTagId(undefined)
    }, [activeImageIds])

    const handleTagSelection = useCallback(
        (ids: string[], idx: number) => {
            if (!imagePreviewType) return
            if (idx === activeTagId) setActiveTagId(undefined)
            else {
                setActiveTagId(idx)
                dispatch(
                    setActiveImageId({
                        ids: Object.fromEntries(ids.map((str) => [str, true])),
                        type: imagePreviewType,
                    })
                )
            }
        },
        [dispatch, activeTagId, imagePreviewType]
    )

    return (
        <div className="flex flex-col gap-1 mt-2">
            {Object.entries(warnings || {})
                .reverse()
                .map(([title, data], idx) => {
                    const { ids, color } = data
                    const count = ids.length
                    if (count === 0) return null
                    return (
                        <Tag
                            key={idx}
                            title={title}
                            color={color}
                            count={ids.length}
                            isActive={activeTagId === idx}
                            onClick={() => handleTagSelection(ids, idx)}
                            setActiveTagId={setActiveTagId}
                        />
                    )
                })}
        </div>
    )
}

const Tag = ({
    title,
    color,
    count,
    isActive,
    setActiveTagId,
    onClick,
}: {
    title: string
    color: string
    count: number
    isActive: boolean
    setActiveTagId: (id?: number) => void
    onClick: () => void
}) => {
    return (
        <ClickAwayListener
            onClick={onClick}
            onClickAway={() => setActiveTagId(undefined)}
            className={classNames(
                "flex items-center justify-between w-full gap-2 px-2 py-1 border border-gray-700 rounded-md cursor-pointer",
                { "bg-blue-700": isActive },
                { "hover:bg-gray-700": !isActive }
            )}
        >
            <div className="flex items-center gap-2 translate-y-[1px]">
                <div
                    className={classNames("h-2 w-2 rounded-[3px]")}
                    style={{ backgroundColor: color }}
                />
                <small className={classNames("text-gray-100", { "!text-gray-0": isActive })}>
                    {title}
                </small>
            </div>
            <small
                className={classNames("text-gray-300 translate-y-[1px]", {
                    "!text-gray-0": isActive,
                })}
            >
                {count}
            </small>
        </ClickAwayListener>
    )
}
